import React, { useState, useEffect } from 'react';
import DynamicSidebar from '../../../components/ui/DynamicSidebar';
import DashboardHeader from '../../../components/ui/DashboardHeader';
import Icon from '../../../components/AppIcon';
import { apiService } from '../../../services/api';

const AccountTypesPage = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [accountTypes, setAccountTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAccountTypes = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await apiService?.get('/portal/financials/account-types');
        const data = response?.data;
        if (data?.status_code === 200 && Array.isArray(data?.details)) {
          setAccountTypes(data?.details);
        } else {
          setError('Failed to load account types.');
        }
      } catch (err) {
        setError(err?.response?.data?.status_description || 'An error occurred while fetching account types.');
      } finally {
        setLoading(false);
      }
    };
    fetchAccountTypes();
  }, []);

  const NormalBalanceBadge = ({ value }) => {
    if (value === 'DEBIT') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
          {value}
        </span>
      );
    }
    if (value === 'CREDIT') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
          {value}
        </span>
      );
    }
    return <span className="text-text-secondary text-sm">{value}</span>;
  };

  return (
    <div className="min-h-screen bg-background">
      <DashboardHeader onMenuToggle={() => setSidebarCollapsed(prev => !prev)} />
      <div className="flex">
        <DynamicSidebar
          isCollapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(prev => !prev)}
        />
        <main
          className={`flex-1 transition-all duration-300 ${
            sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'
          } mt-16 p-6`}
        >
          <div className="mb-6">
            <div className="flex items-center space-x-2 text-sm text-text-secondary mb-1">
              <span>Finance</span>
              <Icon name="ChevronRight" size={14} />
              <span>Setup</span>
              <Icon name="ChevronRight" size={14} />
              <span className="text-text-primary font-medium">Account Types</span>
            </div>
            <h1 className="text-2xl font-bold text-text-primary">Account Types</h1>
            <p className="text-text-secondary text-sm mt-1">Define and manage account types for your financial structure</p>
          </div>

          <div className="bg-surface border border-border rounded-lg overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center p-12">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                <span className="ml-3 text-text-secondary text-sm">Loading account types...</span>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center p-12 text-center">
                <Icon name="AlertCircle" size={32} className="text-red-500 mb-3" />
                <p className="text-text-secondary text-sm">{error}</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface border-b border-border">
                    <th className="text-left px-6 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wider">Type Code</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wider">Type Name</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wider">Normal Balance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {accountTypes?.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="px-6 py-10 text-center text-text-secondary text-sm">No account types found.</td>
                    </tr>
                  ) : (
                    accountTypes?.map((item, index) => (
                      <tr key={index} className="hover:bg-background transition-colors">
                        <td className="px-6 py-4 font-medium text-text-primary">{item?.type_code}</td>
                        <td className="px-6 py-4 text-text-primary">{item?.type_name}</td>
                        <td className="px-6 py-4">
                          <NormalBalanceBadge value={item?.normal_balance} />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AccountTypesPage;
